import React, { useEffect, useState, useCallback } from 'react';
import { useTutorial } from '../../../contexts/TutorialContext';
import './Tutorial.css';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TutorialSpotlightProps {
  padding?: number;
}

export const TutorialSpotlight: React.FC<TutorialSpotlightProps> = ({ padding = 8 }) => {
  const { isActive, getCurrentStep } = useTutorial();
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const currentStep = getCurrentStep();

  // Find target element and calculate spotlight position
  const updateSpotlight = useCallback(() => {
    if (!currentStep?.targetSelector) {
      setRect(null);
      return;
    }

    const element = document.querySelector(currentStep.targetSelector);
    if (!element) {
      console.warn(`[Tutorial] Target element not found: ${currentStep.targetSelector}`);
      setRect(null);
      return;
    }

    const domRect = element.getBoundingClientRect();
    const effectivePadding = currentStep.highlightPadding ?? padding;

    setRect({
      top: domRect.top - effectivePadding + window.scrollY,
      left: domRect.left - effectivePadding + window.scrollX,
      width: domRect.width + effectivePadding * 2,
      height: domRect.height + effectivePadding * 2,
    });

    // Scroll element into view if needed
    if (domRect.top < 0 || domRect.bottom > window.innerHeight) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentStep, padding]);

  // Update spotlight on step change
  useEffect(() => {
    if (isActive && currentStep) {
      // Small delay to let DOM settle
      const timer = setTimeout(() => {
        updateSpotlight();
        setIsVisible(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      setRect(null);
    }
  }, [isActive, currentStep, updateSpotlight]);

  // Update on window resize
  useEffect(() => {
    if (!isActive) return;

    const handleResize = () => updateSpotlight();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isActive, updateSpotlight]);

  if (!isActive || !isVisible) {
    return null;
  }

  // Generate clip-path for spotlight effect
  const generateClipPath = (): string => {
    if (!rect) {
      // No target - full overlay without hole
      return 'none';
    }

    const { top, left, width, height } = rect;
    const viewportWidth = window.innerWidth;
    const viewportHeight = document.documentElement.scrollHeight;

    // Create a polygon that covers entire viewport with a rectangular hole
    // Using inset() for the spotlight hole effect
    return `polygon(
      0% 0%,
      0% 100%,
      ${left}px 100%,
      ${left}px ${top}px,
      ${left + width}px ${top}px,
      ${left + width}px ${top + height}px,
      ${left}px ${top + height}px,
      ${left}px 100%,
      100% 100%,
      100% 0%
    )`;
  };

  return (
    <div
      className="tutorial-spotlight-overlay"
      style={{
        clipPath: rect ? generateClipPath() : 'none',
      }}
    />
  );
};

export default TutorialSpotlight;
